import Phaser from "phaser";
// haptics handled in TransferController
import { Cat } from "../sprites/CatSprite";
import { TransferSystem } from "../systems/TransferSystem";
import { TransferController } from "../systems/TransferController";
import { UserAttempt } from "../../data/LevelAttempt";
import { backendService } from "../../services/backendService";
import { BackgroundImage } from "../BackgroundImage";
import {
  BACKGROUND_DEFAULT,
  BACKGROUNDS,
  BackgroundName,
} from "../../assets/backgrounds";
import { TOOLBAR_HEIGHT } from "../BackgroundImage";
import { CategoryData, CellBounds } from "../../config/types";
import { buildCellBounds } from "../../utils/grid";
import {
  createCatsInCoordinatePlane,
  repositionCatsInCoordinatePlane,
  destroyCats as destroyCatsUtil,
} from "../systems/CatFactory";
import { SHOW_DEBUG } from "../../config";

export class BudgetSceneReact extends Phaser.Scene {
  private cats: Cat[] = [];
  private categories: CategoryData[] = [];
  private cellBounds: CellBounds[] = [];
  private viewWidth: number = 800;
  private viewHeight: number = 600;
  private transferController?: TransferController;
  private selectedCatIndex: number = -1; // maintained for overlay compatibility
  private transferSystem!: TransferSystem; // legacy coin animation helper
  private levelCompleted: boolean = false;
  private currentAttempt: UserAttempt | null = null;
  private pendingFlyIns: number = 0; // track cats still animating in
  private uiTopMargin: number = 0;
  private fixedBounds?: { minSurplus: number; maxSurplus: number }; // Fixed bounds for the level

  // Background chosen for this scene instance
  private chosenBgName: BackgroundName = BACKGROUND_DEFAULT;

  // Category names to cat number mapping
  private static readonly CATEGORY_NAMES = [
    "Rent",
    "Home Insurance",
    "Utilities",
    "Internet & Phone",
    "Groceries",
    "Restaurants",
    "Coffee Shops",
    "Car Payment",
    "Car Insurance",
    "Gas",
    "Transportation",
    "Health",
    "Loans",
    "Clothing",
    "Shopping",
    "Personal Care",
    "Subscriptions",
    "Entertainment",
    "Gifts",
    "Travel",
    "Misc",
  ];

  /**
   * Get cat number for a category name based on its index in the predefined list
   * @param categoryName The name of the category
   * @returns Cat number (1-15)
   */
  private static getCatNumberForCategory(categoryName: string): number {
    const index = BudgetSceneReact.CATEGORY_NAMES.indexOf(categoryName);

    // If category not found, use hash of name as fallback
    if (index === -1) {
      console.warn(
        `Category "${categoryName}" not found in predefined list, using name hash`,
      );
      const hash = categoryName.split("").reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
      }, 0);
      return Math.abs(hash % 15) + 1;
    }

    // Use index modulo 15 + 1 to get cat number (1-15)
    return (index % 15) + 1;
  }

  constructor() {
    super({ key: "BudgetSceneReact" });
  }

  init(data: { categories: CategoryData[]; bgName?: BackgroundName }) {
    // Deep-clone incoming data so mutations during gameplay never affect the
    // original level definition held by React or future scene restarts.
    this.categories = JSON.parse(JSON.stringify(data.categories || []));

    // Reset transient state on every init so zombie selection / win flags
    // never leak between level loads.
    this.selectedCatIndex = -1;
    this.levelCompleted = false;
    this.transferSystem = undefined as any; // will be lazily recreated

    // Setup attempt tracking if user id available
    const uid = this.registry.get("uid");
    const lvlId = this.registry.get("level")?.id;
    const adult = this.registry.get("adultMode") ?? true;
    if (uid && lvlId) {
      if (lvlId !== undefined) {
        this.currentAttempt = new UserAttempt(uid, lvlId, adult);
      }
      this.currentAttempt.start();
    }

    // Choose background for this level – if a bg name is provided (e.g., after a texture load restart) reuse it, otherwise pull next.
    this.chosenBgName = data.bgName ?? BackgroundImage.getNextBackground();
  }

  create() {
    console.log(
      "BudgetSceneReact: Scene created with categories:",
      this.categories,
    );

    // Set up camera
    // Remove solid background; allow underlying HTML/CSS to show through
    this.cameras.main.setBackgroundColor("rgba(0,0,0,0)");
    this.cameras.main.setZoom(1);

    const texKey = "bg_" + this.chosenBgName;
    const textureAvailable = this.textures.exists(texKey);

    // Draw initial background (default if chosen not ready yet)
    const initialBgName = textureAvailable
      ? this.chosenBgName
      : BACKGROUND_DEFAULT;
    const bg = new BackgroundImage(this, initialBgName);

    // Apply cutoff from the initial background
    this.uiTopMargin = bg.cutoffY;
    console.log("uiTopMargin", this.uiTopMargin);
    window.dispatchEvent(
      new CustomEvent("phaser-ui-margin", { detail: this.uiTopMargin }),
    );

    // If chosen texture missing, load asynchronously and swap once ready
    if (!textureAvailable) {
      console.log(
        `Background texture ${texKey} missing – loading asynchronously.`,
      );
      this.load.image(texKey, BACKGROUNDS[this.chosenBgName].path);
      this.load.once("complete", () => {
        // Destroy old background objects
        bg.image.destroy();
        bg.guide.destroy();

        // Draw the desired background
        const newBg = new BackgroundImage(this, this.chosenBgName);
        this.uiTopMargin = newBg.cutoffY;

        // Update layout to respect new cutoff
        this.updateGrid();
        this.repositionCats();

        // Inform React of updated UI margin
        window.dispatchEvent(
          new CustomEvent("phaser-ui-margin", { detail: this.uiTopMargin }),
        );
      });
      this.load.start();
    }

    // Kick off async preload of remaining background images
    this.preloadRemainingBackgrounds();

    // Defer layout & cat creation one tick to ensure scale and textures ready
    this.time.delayedCall(0, () => {
      // Use React's exact screen dimensions from registry for perfect synchronization
      const reactScreenWidth = this.registry.get("reactScreenWidth");
      const reactScreenHeight = this.registry.get("reactScreenHeight");

      if (reactScreenWidth && reactScreenHeight) {
        // Use React's exact dimensions
        this.viewWidth = reactScreenWidth;
        this.viewHeight = reactScreenHeight;
        console.log(
          "Using React's exact dimensions:",
          this.viewWidth,
          "x",
          this.viewHeight,
        );
      } else {
        // Fallback to Phaser's calculation if React dimensions not available
        this.viewWidth = this.scale.width;
        this.viewHeight = this.scale.height - TOOLBAR_HEIGHT; // Match React's height calculation
        console.log(
          "Using Phaser fallback dimensions:",
          this.viewWidth,
          "x",
          this.viewHeight,
        );
      }

      // Calculate grid layout
      this.updateGrid();

      // All assets have already been loaded by PreloadScene, so we can create cats immediately.
      this.createCats();
    });

    // Expose scene to React
    (window as any).PhaserSceneReact = this;

    // Listen for resize events
    this.scale.on("resize", this.handleResize, this);

    // Add background click handler to clear selection
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      // Check if any interactive object was hit
      const hitObjects = this.input.hitTestPointer(pointer);

      // If no interactive objects were hit, it's a background click
      if (hitObjects.length === 0) {
        console.log("Background click detected - clearing selection");
        // Notify React overlay that nothing is selected
        window.dispatchEvent(
          new CustomEvent("phaser-cat-clicked", {
            detail: { index: -1, selectedIndex: -1 },
          }),
        );
      }
    });

    // After cats created/animation setup, notify React of category positions
    this.notifyReactOfUpdate();

    // Overlay visibility will be toggled on once all fly-in animations are done
  }

  private handleResize(gameSize: { width: number; height: number }) {
    // Use React's exact screen dimensions from registry for perfect synchronization
    const reactScreenWidth = this.registry.get("reactScreenWidth");
    const reactScreenHeight = this.registry.get("reactScreenHeight");

    if (reactScreenWidth && reactScreenHeight) {
      // Use React's exact dimensions
      this.viewWidth = reactScreenWidth;
      this.viewHeight = reactScreenHeight;
      console.log(
        "Resize: Using React's exact dimensions:",
        this.viewWidth,
        "x",
        this.viewHeight,
      );
    } else {
      // Fallback to Phaser's calculation if React dimensions not available
      this.viewWidth = gameSize.width;
      this.viewHeight = gameSize.height - TOOLBAR_HEIGHT; // Match React's height calculation
      console.log(
        "Resize: Using Phaser fallback dimensions:",
        this.viewWidth,
        "x",
        this.viewHeight,
      );
    }

    this.updateGrid();
    this.repositionCats();
  }

  private updateGrid() {
    // For coordinate plane, we don't need to pre-calculate cell bounds
    // as they're calculated dynamically based on surplus values
    this.cellBounds = [];
  }

  private createCats() {
    const levelPrecision = this.registry.get("level")?.dial_precision ?? 100;

    // Calculate fixed bounds at level start (only once)
    if (!this.fixedBounds) {
      const surplusValues = this.categories.map(
        (cat) => cat.budgeted - cat.spent,
      );
      const minSurplus = Math.min(...surplusValues);
      const maxSurplus = Math.max(...surplusValues);
      const greatestDifferential = Math.max(
        Math.abs(minSurplus),
        Math.abs(maxSurplus),
      );

      this.fixedBounds = {
        minSurplus: -greatestDifferential - levelPrecision,
        maxSurplus: greatestDifferential + levelPrecision,
      };

      console.log("Fixed bounds calculated for level:", this.fixedBounds);
    }

    this.cats = createCatsInCoordinatePlane(
      this,
      this.categories,
      this.viewWidth,
      this.viewHeight,
      {
        uiTopMargin: this.uiTopMargin,
        onCatClick: (index) =>
          this.transferController?.handleCatClick(this.cats[index]),
        levelPrecision,
        fixedBounds: this.fixedBounds,
      },
    );

    // Add debug graphics to show Phaser boundaries
    if (SHOW_DEBUG) {
      // Phaser screen boundary - positioned to account for toolbar
      const phaserBorder = this.add.rectangle(
        this.viewWidth / 2,
        this.uiTopMargin + (this.scale.height - this.uiTopMargin) / 2,
        this.viewWidth,
        this.scale.height - this.uiTopMargin,
        0x800080, // Purple
        0.1,
      );
      phaserBorder.setStrokeStyle(4, 0x800080);
      phaserBorder.setDepth(999);

      // Top margin boundary
      const topMarginLine = this.add.rectangle(
        this.viewWidth / 2,
        this.uiTopMargin,
        this.viewWidth,
        2,
        0xffa500, // Orange
      );
      topMarginLine.setDepth(999);

      // Debug text
      const debugText = this.add.text(
        10,
        10,
        `Phaser: ${this.viewWidth}x${this.viewHeight}\nTop Margin: ${this.uiTopMargin}px`,
        {
          fontSize: "16px",
          color: "#800080",
          backgroundColor: "#000000",
          padding: { x: 8, y: 4 },
        },
      );
      debugText.setDepth(1000);
    }

    // Setup transfer controller
    this.transferController = new TransferController(
      this,
      null,
      this.cats,
      this.currentAttempt,
      {
        adultMode: true,
        dialPrecision: this.registry.get("level")?.dial_precision ?? 10,
        categories: this.categories,
        checkWin: () => this.categories.every((c) => c.budgeted >= c.spent),
        onCategoriesChanged: () => {
          this.notifyReactOfUpdate();
          // Update the scene's categories and reposition cats
          this.updateCategories(this.categories);
        },
        onWin: () => this.triggerWinCelebration(),
      },
    );
  }

  private repositionCats() {
    const levelPrecision = this.registry.get("level")?.dial_precision ?? 100;
    console.log("repositionCats called with:", {
      catsCount: this.cats.length,
      categories: this.categories.map((c) => ({
        name: c.name,
        surplus: c.budgeted - c.spent,
      })),
      viewWidth: this.viewWidth,
      viewHeight: this.viewHeight,
      uiTopMargin: this.uiTopMargin,
      levelPrecision,
      fixedBounds: this.fixedBounds,
    });
    repositionCatsInCoordinatePlane(
      this.cats,
      this.categories,
      this.viewWidth,
      this.viewHeight,
      this.uiTopMargin,
      levelPrecision,
      this.fixedBounds,
    );
  }

  public updateCategories(categories: CategoryData[]) {
    this.categories = categories;

    // Update existing cats
    this.cats.forEach((cat, index) => {
      const category = categories[index];
      if (!category) return;

      cat.refreshFinancialStatus(category.budgeted, category.spent);
    });

    console.log(
      "[BudgetSceneReact] categories updated",
      JSON.stringify(this.categories),
    );

    // Reposition cats to match new coordinate system
    console.log("Repositioning cats due to category update");
    this.repositionCats();

    // Check win condition (all categories balanced or surplus)
    if (!this.levelCompleted) {
      const isComplete = this.categories.every((c) => c.budgeted >= c.spent);
      if (isComplete) {
        this.levelCompleted = true;
        this.triggerWinCelebration();
      }
    }

    // If category count changed, recreate
    if (categories.length !== this.cats.length) {
      this.destroyCats();
      this.updateGrid();
      this.createCats();
    }

    // --- Auto-deselect logic ------------------------------------------------
    if (this.selectedCatIndex !== -1) {
      const sel = this.categories[this.selectedCatIndex];
      if (sel && sel.budgeted === sel.spent) {
        // Selected donor cat is now balanced – clear selection
        console.log("Selected cat became balanced; deselecting");

        this.selectedCatIndex = -1;

        // Notify React overlay so it stops ring pulse
        window.dispatchEvent(
          new CustomEvent("phaser-cat-clicked", {
            detail: { index: -1, selectedIndex: -1 },
          }),
        );
      }
    }
  }

  /**
   * Refresh heart visibility for all cats based on current hint settings
   * This should be called when hint visibility changes
   */
  public refreshHeartVisibility(): void {
    this.cats.forEach((cat) => {
      cat.refreshHeartVisibility();
    });
  }

  private destroyCats() {
    destroyCatsUtil(this.cats);
  }

  public getCellBounds(): CellBounds[] {
    return this.cellBounds;
  }

  // Selection & transfer handled by TransferController
  // triggerHapticFeedback removed – handled internally

  private notifyReactOfUpdate() {
    // Send current categories and coordinate system debug info to React
    const categoryData = this.categories.map((category, index) => ({
      name: category.name,
      spent: category.spent,
      budgeted: category.budgeted,
      necessary: category.necessary,
      savings: category.savings,
    }));

    // Emit event with current game state and coordinate system debug info
    const event = new CustomEvent("phaser-categories-updated", {
      detail: {
        categories: categoryData,
        adultMode: this.registry.get("adultMode") ?? true,
        level: {
          id: this.registry.get("level")?.id || "unknown",
          nickname: this.registry.get("level")?.nickname || "Unknown Level",
        },
        // Debug: Include Phaser coordinate system info
        debug: {
          phaserDimensions: { width: this.viewWidth, height: this.viewHeight },
          phaserScale: { width: this.scale.width, height: this.scale.height },
          phaserCellBounds: this.cellBounds.map((bounds, index) => ({
            index,
            bounds,
            catPosition: {
              x: bounds.x + bounds.w / 2,
              y: bounds.y + bounds.h / 2,
            },
          })),
        },
      },
    });
    document.dispatchEvent(event);
  }

  // animateCoin delegated via TransferSystem internally; kept for React API but simplified.
  public animateCoin(fromIndex: number, toIndex: number, amount: number) {
    if (this.transferSystem) {
      this.transferSystem.transfer(
        fromIndex,
        toIndex,
        amount,
        new Phaser.Math.Vector2(0, 0),
        new Phaser.Math.Vector2(0, 0),
      );
    }
  }

  /** Simple celebration: all cats jump and notify React */
  private triggerWinCelebration() {
    // Make all cats jump in joy
    this.cats.forEach((cat, idx) => {
      const delay = idx * 150;
      this.time.delayedCall(delay, () => cat.jumpInJoy());
    });

    // Finish attempt and save, then notify React
    (async () => {
      let tokensEarned = 0;
      let mistakes: string[] = [];
      try {
        if (this.currentAttempt) {
          // Mark attempt as completed BEFORE save so "completed" timestamp is set
          const finalAdult = this.registry.get("adultMode") ?? true;
          this.currentAttempt.complete(0, 0, finalAdult);

          const response = await backendService.saveAttempt(
            this.currentAttempt,
          );
          console.log("Attempt saved", response);

          // Record real token award and mistakes
          tokensEarned = response.tokens;
          mistakes = response.mistakes;
          this.currentAttempt.tokensAwarded = tokensEarned;
          this.currentAttempt.mistakes = mistakes;
        }
      } catch (err) {
        console.error("Failed to save attempt", err);
      } finally {
        // After brief pause so users see cat celebration
        this.time.delayedCall(600, () => {
          const evt = new CustomEvent("levelComplete", {
            detail: {
              score: this.currentAttempt?.score ?? 0,
              tokens: tokensEarned,
              mistakes: mistakes,
              moves: this.currentAttempt?.moves ?? 0,
              duration: this.currentAttempt?.duration ?? 0,
            },
          });
          window.dispatchEvent(evt);
        });
      }
    })();
  }

  /** Preload all background images except the default in the background without blocking the scene */
  private preloadRemainingBackgrounds() {
    // Backgrounds are now loaded by PreloadScene
  }
}
